/**
 * O e-mail do monitoramento — o liga/desliga, AQUI (pedido de 10/09/2026).
 *
 * 🔴 O interruptor MUDOU de casa: morava em Configurações › Monitoramento e o
 * Matheus pediu para habilitar/desabilitar "na mesma tela de whatsapp
 * integrações" — os avisos automáticos moram juntos. Uma voz só: lá ficou o
 * aviso de onde ele está agora; o interruptor existe SÓ aqui.
 *
 * ⚠️ Mesmo mecanismo de sempre (PATCH /monitoramento/avisos-email): desligado,
 * nada é enviado — e religar NÃO dispara os avisos do período desligado.
 */
import { useEffect, useState } from 'react';

import { Alert, Card, HelpTip, Switch } from '@/components/ui';
import { api, ApiError } from '@/lib/api';

interface MedicaoGlobal {
  avisosEmailLigado: boolean;
}

export function CartaoEmailMonitoramento() {
  const [ligado, setLigado] = useState<boolean | null>(null);
  const [semAcesso, setSemAcesso] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api
      .get<{ medicao?: MedicaoGlobal }>('/api/v1/monitoramento/config')
      .then((r) => setLigado(r.medicao?.avisosEmailLigado ?? null))
      .catch((e: unknown) => {
        // Sem permissão o cartão some — a segurança real é do servidor.
        if (e instanceof ApiError && e.status === 403) setSemAcesso(true);
        else setErro(e instanceof Error ? e.message : 'Não foi possível consultar.');
      });
  }, []);

  if (semAcesso) return null;

  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid="integracao-email-monitoramento">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-display text-xl text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
            E-mail do monitoramento
            <HelpTip texto="O aviso que a equipe da Manutenção recebe quando um equipamento cai ou volta — um e-mail por evento, nunca um por equipamento, no mesmo layout do resumo financeiro. Desligado, nada é enviado; religar não dispara os avisos do período desligado." />
          </h2>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3">
            Queda e volta de equipamentos, para a equipe da Manutenção — remetente no-reply.
          </p>
        </div>
        {ligado === null && !erro ? (
          <span className="text-sm text-ink-3 dark:text-ink-dark-3">Consultando…</span>
        ) : ligado !== null ? (
          <Switch
            checked={ligado}
            disabled={salvando}
            label={ligado ? 'Ligado' : 'Desligado'}
            data-testid="email-monitoramento-toggle"
            onChange={async (e) => {
              const querLigar = e.target.checked;
              setErro(null);
              setSalvando(true);
              try {
                const r = await api.patch<{ medicao: MedicaoGlobal }>(
                  '/api/v1/monitoramento/avisos-email',
                  { ligado: querLigar },
                );
                setLigado(r.medicao.avisosEmailLigado);
              } catch (err) {
                setErro(err instanceof Error ? err.message : 'Não foi possível mudar agora.');
              } finally {
                setSalvando(false);
              }
            }}
          />
        ) : null}
      </div>
      {erro && <Alert variant="danger">{erro}</Alert>}
    </Card>
  );
}
